import { useEffect, useRef, useState } from "react";
import { useAppContext } from "../../../../AppContext";
import { useApiMutation } from "../../../infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../infrastructure/routes/endpointsMetadata";
import { OTP_CODE_LENGTH, OtpVerificationInput } from "../../OtpVerificationInput/OtpVerificationInput";
import { IUserModel } from "../../../infrastructure/hooks/useAuthentication";
import styles from "./EmailVerificationStep.module.scss";

interface IEmailVerificationStepProps {
  maskedEmail: string;
  phoneNumber: string;
  onVerified: (loginResponse: IUserModel) => void;
  onBack?: () => void;
}

const RESEND_COOLDOWN_SECONDS = 30;
const MAX_RESEND_COUNT = 4;

export const EmailVerificationStep = ({
  maskedEmail,
  phoneNumber,
  onVerified,
  onBack,
}: IEmailVerificationStepProps) => {
  const { selectedStudio } = useAppContext();
  const [code, setCode] = useState("");
  const [error, setError] = useState<string>();
  const [countdown, setCountdown] = useState(RESEND_COOLDOWN_SECONDS);
  const [resendCount, setResendCount] = useState(0);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval>>();

  const { mutateAsync: verifyMutate } = useApiMutation<IUserModel>();
  const { mutateAsync: resendMutate } = useApiMutation();

  useEffect(() => {
    startCountdown();
    return () => clearInterval(intervalRef.current);
  }, []);

  const startCountdown = () => {
    setCountdown(RESEND_COOLDOWN_SECONDS);
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleVerify = async (value: string) => {
    if (value.length !== OTP_CODE_LENGTH) return;
    setIsVerifying(true);
    setError(undefined);

    try {
      const result = await verifyMutate({
        endpoint: endpointsMetadata.authentication.verifyEmailCode,
        method: "post",
        disableSuccessToast: true,
        globalIsLoading: false,
        payload: { phoneNumber, code: value },
        queryParams: { studioId: selectedStudio?.id },
      });

      onVerified(result);
    } catch (e: any) {
      const message =
        e?.response?.data?.message ??
        e?.response?.data ??
        "Невалиден код. Моля, опитайте отново.";
      setError(
        typeof message === "string"
          ? message
          : "Невалиден код. Моля, опитайте отново.",
      );
      setCode("");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0 || resendCount >= MAX_RESEND_COUNT || isResending) return;

    setIsResending(true);
    try {
      await resendMutate({
        endpoint: endpointsMetadata.authentication.resendVerificationCode,
        method: "post",
        disableSuccessToast: true,
        globalIsLoading: false,
        payload: { phoneNumber },
        queryParams: { studioId: selectedStudio?.id },
      });

      setResendCount((prev) => prev + 1);
      setCode("");
      setError(undefined);
      startCountdown();
    } catch {
    } finally {
      setIsResending(false);
    }
  };

  const formatCountdown = (seconds: number) =>
    `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;

  return (
    <div className={styles.container}>
      <p className={styles.subtitle}>
        Изпратихме {OTP_CODE_LENGTH}-цифрен код на <strong>{maskedEmail}</strong>. Кодът е
        валиден 30 минути.
      </p>

      <div className={styles.inputWrapper}>
        <OtpVerificationInput
          value={code}
          onChange={setCode}
          onComplete={handleVerify}
          disabled={isVerifying}
          error={!!error}
        />
      </div>

      {error && <p className={styles.errorText}>{error}</p>}

      <p className={styles.spamNotice}>
        Не намирате имейла? Проверете папката за спам.
      </p>

      <div className={styles.resendSection}>
        {resendCount >= MAX_RESEND_COUNT ? (
          <div className={styles.contactPrompt}>
            Ако продължавате да имате проблеми, свържете се с обекта за
            потвърждение на акаунта.
          </div>
        ) : countdown > 0 ? (
          <p className={styles.countdown}>
            Повторно изпращане след{" "}
            <span className={styles.countdownTimer}>
              {formatCountdown(countdown)}
            </span>
          </p>
        ) : (
          <button
            type="button"
            className={styles.resendButton}
            onClick={handleResend}
            disabled={isResending}
          >
            {isResending ? (
              <span className={styles.resendSpinner} />
            ) : (
              "Изпрати отново"
            )}
          </button>
        )}
      </div>

      {onBack && (
        <button type="button" className={styles.backButton} onClick={onBack}>
          ← Назад
        </button>
      )}
    </div>
  );
};
